<template>
  <!-- 다운로드 페이지 에디터 -->
  <div class="download-page-editor">
    <!-- 상단 헤더 -->
    <div class="download-page-editor__header">
      <h3 class="download-page-editor__title">다운로드 앱 관리</h3>
      <a-button
        type="primary"
        class="yellow-button"
        @click="handleAdd"
      >
        + 다운로드 추가
      </a-button>
    </div>

    <!-- 탭: 병원용 / 고객용 -->
    <a-tabs v-model:activeKey="activeTab" @change="handleTabChange">
      <!-- 병원용 탭 -->
      <a-tab-pane key="hospital" tab="병원용">
        <div class="tab-content">
          <!-- 빈 상태 -->
          <a-empty
            v-if="filteredApps('hospital').length === 0"
            description="등록된 병원용 앱이 없습니다."
            style="margin-top: 40px;"
          />
          <!-- 앱 카드 그리드 -->
          <a-row v-else :gutter="16">
            <a-col
              v-for="(app, index) in filteredApps('hospital')"
              :key="app._id || app.id || index"
              :span="12"
              style="margin-bottom: 16px;"
            >
              <a-card
                class="app-card"
                hoverable
                @click="handleEdit(app, index, 'hospital')"
              >
                <!-- 앱 아이콘 + 이름 -->
                <div class="app-card__top">
                  <img
                    v-if="app.iconSrc"
                    :src="app.iconSrc"
                    class="app-card__icon"
                    alt="앱 아이콘"
                  />
                  <div v-else class="app-card__icon-placeholder">
                    <appstore-outlined />
                  </div>
                  <div class="app-card__info">
                    <strong class="app-card__name">{{ app.appName }}</strong>
                    <div class="app-card__meta">
                      <a-tag :color="getPlatformColor(app.platform)">
                        {{ app.platform || '플랫폼 미설정' }}
                      </a-tag>
                      <a-tag :color="app.isActive ? 'green' : 'default'">
                        {{ app.isActive ? '활성' : '비활성' }}
                      </a-tag>
                    </div>
                  </div>
                </div>

                <!-- 버전 + URL -->
                <div class="app-card__detail">
                  <span v-if="app.version" class="app-card__version">
                    v{{ app.version }}
                  </span>
                  <span v-if="app.downloadUrl" class="app-card__url">
                    {{ truncateUrl(app.downloadUrl) }}
                  </span>
                </div>

                <!-- 클릭 안내 -->
                <div class="app-card__edit-hint">
                  <edit-outlined /> 클릭하여 편집
                </div>
              </a-card>
            </a-col>
          </a-row>
        </div>
      </a-tab-pane>

      <!-- 고객용 탭 -->
      <a-tab-pane key="customer" tab="고객용">
        <div class="tab-content">
          <!-- 빈 상태 -->
          <a-empty
            v-if="filteredApps('customer').length === 0"
            description="등록된 고객용 앱이 없습니다."
            style="margin-top: 40px;"
          />
          <!-- 앱 카드 그리드 -->
          <a-row v-else :gutter="16">
            <a-col
              v-for="(app, index) in filteredApps('customer')"
              :key="app._id || app.id || index"
              :span="12"
              style="margin-bottom: 16px;"
            >
              <a-card
                class="app-card"
                hoverable
                @click="handleEdit(app, index, 'customer')"
              >
                <!-- 앱 아이콘 + 이름 -->
                <div class="app-card__top">
                  <img
                    v-if="app.iconSrc"
                    :src="app.iconSrc"
                    class="app-card__icon"
                    alt="앱 아이콘"
                  />
                  <div v-else class="app-card__icon-placeholder">
                    <appstore-outlined />
                  </div>
                  <div class="app-card__info">
                    <strong class="app-card__name">{{ app.appName }}</strong>
                    <div class="app-card__meta">
                      <a-tag :color="getPlatformColor(app.platform)">
                        {{ app.platform || '플랫폼 미설정' }}
                      </a-tag>
                      <a-tag :color="app.isActive ? 'green' : 'default'">
                        {{ app.isActive ? '활성' : '비활성' }}
                      </a-tag>
                    </div>
                  </div>
                </div>

                <!-- 버전 + URL -->
                <div class="app-card__detail">
                  <span v-if="app.version" class="app-card__version">
                    v{{ app.version }}
                  </span>
                  <span v-if="app.downloadUrl" class="app-card__url">
                    {{ truncateUrl(app.downloadUrl) }}
                  </span>
                </div>

                <!-- 클릭 안내 -->
                <div class="app-card__edit-hint">
                  <edit-outlined /> 클릭하여 편집
                </div>
              </a-card>
            </a-col>
          </a-row>
        </div>
      </a-tab-pane>
    </a-tabs>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { EditOutlined, AppstoreOutlined } from '@ant-design/icons-vue';

// 플랫폼별 태그 색상
const PLATFORM_COLORS = {
  iOS: 'blue',
  Android: 'green',
  Windows: 'purple',
  macOS: 'orange',
  Web: 'cyan',
};

export default {
  name: 'DownloadPageEditor',

  components: {
    EditOutlined,
    AppstoreOutlined,
  },

  data() {
    return {
      // 현재 활성 탭 (병원용 / 고객용)
      activeTab: 'hospital',
    };
  },

  computed: {
    ...mapGetters('homepageEditor', ['currentPageData']),

    // 전체 다운로드 앱 목록
    allApps() {
      return this.currentPageData || [];
    },
  },

  methods: {
    ...mapMutations('homepageEditor', ['SET_ACTIVE_EDITOR']),

    // 카테고리별 앱 필터
    filteredApps(category) {
      return this.allApps.filter((app) => app.category === category);
    },

    // 탭 변경 핸들러
    handleTabChange(key) {
      this.activeTab = key;
    },

    // 앱 추가 버튼: 현재 탭의 category로 신규 편집기 열기
    handleAdd() {
      this.SET_ACTIVE_EDITOR({
        type: 'modal',
        sectionKey: 'downloadApp',
        payload: { app: null, appIndex: -1, category: this.activeTab },
      });
    },

    // 기존 앱 카드 클릭: 편집 모달 열기
    handleEdit(app, appIndex, category) {
      this.SET_ACTIVE_EDITOR({
        type: 'modal',
        sectionKey: 'downloadApp',
        payload: { app, appIndex, category },
      });
    },

    // 플랫폼별 태그 색상 반환
    getPlatformColor(platform) {
      return PLATFORM_COLORS[platform] || 'default';
    },

    // URL 축약 (50자 이상이면 말줄임)
    truncateUrl(url) {
      if (!url) return '';
      return url.length > 50 ? url.substring(0, 47) + '...' : url;
    },
  },
};
</script>

<style scoped>
.download-page-editor__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.download-page-editor__title {
  font-size: 18px;
  font-weight: 600;
  color: #222;
  margin: 0;
}

.tab-content {
  padding-top: 16px;
}

.app-card {
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.app-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.app-card__top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.app-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  border: 1px solid #eee;
  object-fit: cover;
  flex-shrink: 0;
}

.app-card__icon-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #ccc;
  flex-shrink: 0;
}

.app-card__info {
  flex: 1;
}

.app-card__name {
  display: block;
  font-size: 15px;
  color: #222;
  margin-bottom: 6px;
}

.app-card__meta {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.app-card__detail {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}

.app-card__version {
  font-size: 12px;
  color: #888;
}

.app-card__url {
  font-size: 11px;
  color: #bbb;
  word-break: break-all;
}

.app-card__edit-hint {
  font-size: 12px;
  color: #bbb;
  text-align: right;
}
</style>
